/**
 * PSSP LABS — "PSSP Precision" theme
 * horizontal-gallery.js — <pssp-hgallery> scroll-driven horizontal gallery.
 *
 * Used by: sections/project-horizontal-gallery.liquid
 *
 * Contract (BUILD-SPEC §4/§9):
 * - Desktop (≥ 990px) with motion allowed: the host element becomes a tall
 *   scroll region; the inner [data-hgallery-sticky] wrapper pins on screen
 *   and the [data-hgallery-track] is translated horizontally in proportion
 *   to the document scroll progress through the host, on a rAF-gated
 *   passive scroll listener. Native scrolling only — the wheel is NEVER
 *   hijacked and no event is preventDefault-ed.
 * - Everywhere else (mobile/tablet, reduced motion, lite perf mode,
 *   animations off, content that fits): a plain overflow-x row (pure CSS —
 *   the pinned styles only apply under the .hgallery--pinned class this
 *   element adds). Without JS the section is a scrollable row by default.
 * - Maintains the "03 / 08" index counter ([data-hgallery-counter]) and a
 *   --progress custom property (0..1) on the host for the measurement-line
 *   garnish.
 * - Debounced resize recompute, IntersectionObserver work gate (translation
 *   math only runs near the viewport), full cleanup in
 *   disconnectedCallback(), and shopify:block:select alignment for the
 *   theme editor.
 */

if (!customElements.get('pssp-hgallery')) {
  customElements.define(
    'pssp-hgallery',
    class extends HTMLElement {
      connectedCallback() {
        this._sticky = this.querySelector('[data-hgallery-sticky]');
        this._viewport = this.querySelector('[data-hgallery-viewport]');
        this._track = this.querySelector('[data-hgallery-track]');
        this._counter = this.querySelector('[data-hgallery-counter]');
        this._slides = Array.prototype.slice.call(
          this.querySelectorAll('[data-hgallery-slide]')
        );
        if (!this._sticky || !this._viewport || !this._track || this._slides.length === 0) {
          return;
        }

        this._mode = null;
        this._distance = 0;
        this._stickyTop = 0;
        this._lastProgress = -1;
        this._lastIndex = -1;
        this._inView = false;
        this._raf = null;

        this._mqDesktop = window.matchMedia('(min-width: 990px)');
        this._mqMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        /* Re-evaluate the mode on breakpoint or motion-preference changes. */
        this._onMediaChange = () => this._refresh();
        this._bindMedia(this._mqDesktop, this._onMediaChange);
        this._bindMedia(this._mqMotion, this._onMediaChange);

        this._onResize = this._makeDebounced(() => this._refresh(), 200);
        window.addEventListener('resize', this._onResize);

        /* Theme editor: bring the selected slide block into view. */
        this._onBlockSelect = (event) => {
          if (!this.contains(event.target)) return;
          const slide = event.target.closest('[data-hgallery-slide]');
          if (slide) this._alignTo(slide);
        };
        document.addEventListener('shopify:block:select', this._onBlockSelect);

        this._refresh();
      }

      disconnectedCallback() {
        if (!this._track) return; /* connectedCallback bailed early */
        window.removeEventListener('resize', this._onResize);
        document.removeEventListener('shopify:block:select', this._onBlockSelect);
        this._unbindMedia(this._mqDesktop, this._onMediaChange);
        this._unbindMedia(this._mqMotion, this._onMediaChange);
        this._exitMode();
        this._onResize = null;
        this._onBlockSelect = null;
        this._onMediaChange = null;
      }

      /* ----- mode management ------------------------------------------- */

      /** Pick pinned vs static from breakpoint + motion + actual overflow. */
      _refresh() {
        if (!this.isConnected) return;
        const overflow = this._track.scrollWidth - this._viewport.clientWidth;
        const wantPinned =
          this._slides.length > 1 &&
          this._mqDesktop.matches &&
          this._motionOK() &&
          overflow > 24;

        if (wantPinned && this._mode !== 'pinned') {
          this._exitMode();
          this._enterPinned();
        } else if (!wantPinned && this._mode !== 'static') {
          this._exitMode();
          this._enterStatic();
        } else if (this._mode === 'pinned') {
          /* Same mode, new layout — remeasure and reapply. */
          this._measure();
          this._lastProgress = -1;
          this._updatePinned();
        } else if (this._mode === 'static') {
          this._updateStatic(true);
        }
      }

      _enterPinned() {
        this._mode = 'pinned';
        this._lastProgress = -1;
        this._lastIndex = -1;
        this.classList.add('hgallery--pinned');
        this._measure();

        /* Passive, rAF-gated document scroll — never intercepts the wheel. */
        this._onScroll = () => {
          if (!this._inView || this._raf) return;
          this._raf = window.requestAnimationFrame(() => {
            this._raf = null;
            this._updatePinned();
          });
        };
        window.addEventListener('scroll', this._onScroll, { passive: true });

        /* Work gate: only translate while the gallery is near the viewport. */
        this._io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this._inView = entry.isIntersecting;
              if (entry.isIntersecting) this._updatePinned();
            });
          },
          { rootMargin: '200px 0px' }
        );
        this._io.observe(this);

        this._updatePinned();
      }

      _enterStatic() {
        this._mode = 'static';
        this._lastIndex = -1;

        this._onViewportScroll = () => {
          if (this._raf) return;
          this._raf = window.requestAnimationFrame(() => {
            this._raf = null;
            this._updateStatic(false);
          });
        };
        this._viewport.addEventListener('scroll', this._onViewportScroll, {
          passive: true
        });

        this._updateStatic(true);
      }

      /** Tear down whichever mode is active and reset every inline style. */
      _exitMode() {
        if (this._raf) {
          window.cancelAnimationFrame(this._raf);
          this._raf = null;
        }
        if (this._mode === 'pinned') {
          window.removeEventListener('scroll', this._onScroll);
          this._onScroll = null;
          if (this._io) {
            this._io.disconnect();
            this._io = null;
          }
          this.classList.remove('hgallery--pinned');
          this.style.height = '';
          this._track.style.transform = '';
          this._inView = false;
        } else if (this._mode === 'static') {
          this._viewport.removeEventListener('scroll', this._onViewportScroll);
          this._onViewportScroll = null;
        }
        this.style.removeProperty('--progress');
        this._mode = null;
      }

      /* ----- pinned mode ------------------------------------------------ */

      /**
       * Horizontal distance = track overflow. The host grows by exactly that
       * distance so vertical scroll maps 1:1 onto horizontal translation.
       */
      _measure() {
        this._distance = Math.max(
          this._track.scrollWidth - this._viewport.clientWidth,
          0
        );
        this._stickyTop =
          parseFloat(window.getComputedStyle(this._sticky).top) || 0;
        this.style.height = `${Math.ceil(
          this._sticky.offsetHeight + this._distance
        )}px`;
      }

      _updatePinned() {
        if (!this.isConnected || this._mode !== 'pinned' || this._distance <= 0) return;
        const top = this.getBoundingClientRect().top;
        let progress = (this._stickyTop - top) / this._distance;
        progress = Math.min(Math.max(progress, 0), 1);
        if (Math.abs(progress - this._lastProgress) < 0.0005) return;
        this._lastProgress = progress;

        this._track.style.transform = `translate3d(${(
          -progress * this._distance
        ).toFixed(2)}px, 0, 0)`;
        this.style.setProperty('--progress', progress.toFixed(4));
        this._setCounter(Math.round(progress * (this._slides.length - 1)));
      }

      /* ----- static mode -------------------------------------------------- */

      _updateStatic(force) {
        if (!this.isConnected || this._mode !== 'static') return;
        const vp = this._viewport;
        const maxScroll = Math.max(vp.scrollWidth - vp.clientWidth, 0);
        const ratio =
          maxScroll > 0 ? Math.min(Math.max(vp.scrollLeft / maxScroll, 0), 1) : 0;
        this.style.setProperty('--progress', ratio.toFixed(4));

        /* Nearest slide to the current scroll offset drives the counter. */
        const trackLeft = this._track.getBoundingClientRect().left;
        let best = 0;
        let bestDistance = Infinity;
        this._slides.forEach((slide, i) => {
          const left = slide.getBoundingClientRect().left - trackLeft;
          const d = Math.abs(left - vp.scrollLeft);
          if (d < bestDistance) {
            bestDistance = d;
            best = i;
          }
        });
        if (force) this._lastIndex = -1;
        this._setCounter(best);
      }

      /* ----- shared helpers ------------------------------------------------ */

      /** Scroll the document (pinned) or the row (static) to a slide. */
      _alignTo(slide) {
        const slideLeft =
          slide.getBoundingClientRect().left -
          this._track.getBoundingClientRect().left;

        if (this._mode === 'pinned' && this._distance > 0) {
          const progress = Math.min(Math.max(slideLeft / this._distance, 0), 1);
          const hostTop = this.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({
            top: Math.round(hostTop - this._stickyTop + progress * this._distance),
            behavior: 'auto'
          });
          this._updatePinned();
        } else {
          this._viewport.scrollTo({ left: Math.max(slideLeft, 0), behavior: 'auto' });
        }
      }

      /** Write "03 / 08" into the counter (decorative, aria-hidden). */
      _setCounter(index) {
        if (!this._counter || index === this._lastIndex) return;
        this._lastIndex = index;
        this._counter.textContent = `${this._pad(index + 1)} / ${this._pad(
          this._slides.length
        )}`;
      }

      _pad(value) {
        return String(value).padStart(2, '0');
      }

      _motionOK() {
        if (window.PSSPTheme && typeof window.PSSPTheme.motionOK === 'function') {
          return window.PSSPTheme.motionOK();
        }
        return !this._mqMotion.matches;
      }

      _makeDebounced(fn, wait) {
        if (window.PSSPTheme && typeof window.PSSPTheme.debounce === 'function') {
          return window.PSSPTheme.debounce(fn, wait);
        }
        let timer = null;
        return function debounced(...args) {
          clearTimeout(timer);
          timer = setTimeout(() => fn.apply(this, args), wait);
        };
      }

      _bindMedia(mql, handler) {
        if (typeof mql.addEventListener === 'function') {
          mql.addEventListener('change', handler);
        } else if (typeof mql.addListener === 'function') {
          mql.addListener(handler);
        }
      }

      _unbindMedia(mql, handler) {
        if (!mql || !handler) return;
        if (typeof mql.removeEventListener === 'function') {
          mql.removeEventListener('change', handler);
        } else if (typeof mql.removeListener === 'function') {
          mql.removeListener(handler);
        }
      }
    }
  );
}
